import React from 'react';
import {
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { Typography, Box, Paper, CircularProgress } from '@mui/material';

const ExpenseReportComplianceGraph = ({ data }) => {
  if (!data) {
    return (
      <Box sx={{ 
        width: '100%', 
        height: 400, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Transform the data into the format expected by the chart
  const chartData = [
    {
      name: 'On Time',
      value: parseInt(data.on_time) || 0,
      fill: '#4caf50' // Green
    },
    {
      name: 'Approaching Deadline',
      value: parseInt(data.approaching) || 0,
      fill: '#ff9800' // Orange/Yellow
    },
    {
      name: 'Overdue',
      value: parseInt(data.overdue) || 0,
      fill: '#f44336' // Red
    }
  ];

  // Calculate percentages for the subtitle
  const total = parseInt(data.total_users) || 1; // Prevent division by zero
  const percentages = {
    onTime: Math.round(((parseInt(data.on_time) || 0) / total) * 100),
    approaching: Math.round(((parseInt(data.approaching) || 0) / total) * 100),
    overdue: Math.round(((parseInt(data.overdue) || 0) / total) * 100)
  };

  // Get month name for the target month (previous month)
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const targetMonth = data.target_month ? monthNames[data.target_month - 1] : '';
  const targetYear = data.target_year || '';
  
  // Format deadline date if available
  let deadlineDisplay = '';
  if (data.deadline_date) {
    const deadlineDate = new Date(data.deadline_date);
    deadlineDisplay = deadlineDate.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric'
    });
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value } = payload[0].payload;
      const percentage = Math.round((value / total) * 100);
      return (
        <Paper sx={{ p: 1, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
          <Typography variant="body2">{`${name}: ${value} users`}</Typography>
          <Typography variant="body2">{`${percentage}% of total`}</Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Typography variant="h6" align="center" gutterBottom>
        Monthly Expense Report Compliance
        {targetMonth && targetYear && (
          <Typography variant="subtitle1" component="span" sx={{ display: 'block', mt: 1 }}>
            {targetMonth} {targetYear} Reports
          </Typography>
        )}
      </Typography>
      
      <Box sx={{ width: '100%', height: 400, position: 'relative' }}>
        <Box sx={{ position: 'absolute', width: '100%', height: '100%' }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                dataKey="value"
                name="Number of Users"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="textSecondary" align="center">
          Total Users: {total} | On Time: {percentages.onTime}% | 
          Approaching: {percentages.approaching}% | Overdue: {percentages.overdue}%
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block" align="center" sx={{ mt: 1 }}>
          * Expense reports for the previous month are due by the end of the first full workweek of the current month.
          {deadlineDisplay && ` Current deadline: ${deadlineDisplay}.`}
        </Typography>
      </Box>
    </Box>
  );
};

export default ExpenseReportComplianceGraph;